import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import UserActivityService from '../services/userActivityService';
import { isAdminUser } from '../config/adminConfig';

const AdminTestPage: React.FC = () => {
  const { user } = useAuth();
  const [onlineUsers, setOnlineUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAdmin = isAdminUser(user?.email, user?.name);

  const testAdminAPI = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/admin/user-stats', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Admin API Response:', data);
      
      setOnlineUsers(data.userStats || []);
    } catch (err) {
      console.error('Admin API Error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  const testActivityService = async () => {
    try {
      const activityService = UserActivityService.getInstance();
      const users = await activityService.getOnlineUsers();
      console.log('Online Users:', users);
      setOnlineUsers(users);
    } catch (err) {
      console.error('Activity Service Error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  const testUpdateActivity = async () => {
    try {
      const activityService = UserActivityService.getInstance();
      if (user) {
        activityService.recordActivity(user.uid);
        console.log('Activity recorded for user:', user.uid);
      }
    } catch (err) {
      console.error('Update Activity Error:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Alert severity="error">
          <Typography variant="h6">權限不足</Typography>
          <Typography>您沒有權限訪問此測試頁面</Typography>
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" sx={{ mb: 3 }}>
        管理員功能測試
      </Typography>

      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button 
          variant="contained" 
          onClick={testAdminAPI}
          disabled={loading}
        >
          測試管理員API
        </Button>
        
        <Button 
          variant="contained" 
          onClick={testActivityService}
          disabled={loading}
        >
          測試活動服務
        </Button>
        
        <Button 
          variant="contained" 
          onClick={testUpdateActivity}
          disabled={loading}
        >
          記錄活動
        </Button>
      </Box>

      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CircularProgress size={20} />
          <Typography>載入中...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          在線用戶 ({onlineUsers.length})
        </Typography>
        
        {onlineUsers.length > 0 ? (
          <Box>
            {onlineUsers.map((user, index) => (
              <Box key={index} sx={{ p: 2, border: '1px solid #ccc', mb: 1, borderRadius: 1 }}>
                <Typography><strong>ID:</strong> {user.userId || user.id}</Typography>
                <Typography><strong>名稱:</strong> {user.name}</Typography>
                <Typography><strong>Email:</strong> {user.email}</Typography>
                <Typography><strong>狀態:</strong> {user.status || 'online'}</Typography>
                {user.stayDuration && (
                  <Typography><strong>停留時長:</strong> {user.stayDuration}</Typography>
                )}
                {user.videoCount !== undefined && (
                  <Typography><strong>影片數:</strong> {user.videoCount}</Typography>
                )}
                {user.checkpointCount !== undefined && (
                  <Typography><strong>重點數:</strong> {user.checkpointCount}</Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography color="text.secondary">暫無在線用戶</Typography>
        )}
      </Box>
    </Box>
  );
};

export default AdminTestPage;
